import {determineLocation} from './determineLocation';

export interface FilingAvailability {
    enabled: boolean;
    annualReportsEnabled: boolean;
    stripePublishableKey: string | null;
}

export const closedFilings: FilingAvailability = {enabled: false, annualReportsEnabled: false, stripePublishableKey: null};

/** The server could not be reached or did not answer properly; this is not the same as filings being closed. */
export class FilingAvailabilityUnreachable extends Error {
    constructor(message = 'The filing server could not be reached.') {
        super(message);
        this.name = 'FilingAvailabilityUnreachable';
    }
}

/**
 * One availability request. Filings count as closed only when the server answers 200 and does not open them;
 * a timeout, network error, error status or unreadable answer throws FilingAvailabilityUnreachable instead.
 */
export async function getFilingAvailability(signal: AbortSignal): Promise<FilingAvailability> {
    let settings: any;
    try {
        const response = await fetch(`${determineLocation()}/filing-availability`, {signal, cache: 'no-store'});
        if (response.status !== 200) throw new FilingAvailabilityUnreachable(`The filing server answered ${response.status}.`);
        settings = (await response.json())?.message;
    } catch (error) {
        throw error instanceof FilingAvailabilityUnreachable ? error : new FilingAvailabilityUnreachable();
    }
    if (settings?.enabled !== true || typeof settings.stripePublishableKey !== 'string'
        || !/^pk_(live|test)_[A-Za-z0-9]+$/.test(settings.stripePublishableKey)) return closedFilings;
    return {
        enabled: true,
        annualReportsEnabled: settings.annualReportsEnabled === true,
        stripePublishableKey: settings.stripePublishableKey,
    };
}

const wait = (ms: number, signal: AbortSignal) => new Promise<void>(resolve => {
    if (signal.aborted) return resolve();
    const timer = setTimeout(resolve, ms);
    signal.addEventListener('abort', () => {clearTimeout(timer); resolve();}, {once: true});
});

/** Availability with a time limit per attempt, retrying while the server cannot be reached. */
export async function loadFilingAvailability(signal: AbortSignal, {timeouts = [10000, 15000, 15000], retryDelay = 1000}: {
    timeouts?: number[];
    retryDelay?: number;
} = {}): Promise<FilingAvailability> {
    let failure: unknown = new FilingAvailabilityUnreachable();
    for (const [attempt, timeout] of timeouts.entries()) {
        if (attempt > 0) await wait(retryDelay, signal);
        if (signal.aborted) break;
        const controller = new AbortController();
        const abort = () => controller.abort();
        signal.addEventListener('abort', abort, {once: true});
        const timer = setTimeout(abort, timeout);
        try {
            return await getFilingAvailability(controller.signal);
        } catch (error) {
            failure = error;
        } finally {
            clearTimeout(timer);
            signal.removeEventListener('abort', abort);
        }
    }
    throw failure;
}
